import { BaseEntity } from './common';

/**
 * ユーザーロール
 */
export enum UserRole {
  ADMIN = "ADMIN",
  CUSTOMER = "CUSTOMER",
  CAST = "CAST",
  DRIVER = "DRIVER",
  DISPATCHER = "DISPATCHER",
  MANAGER = "MANAGER"
}

/**
 * ユーザーステータス
 */
export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
  SUSPENDED = 'SUSPENDED'
}

/**
 * ユーザー基本インターフェイス
 */
export interface BaseUser {
  id: number;
  username: string;
  email: string;
  phone_number?: string;
  line_account?: string;
  role: UserRole | string;
  profile_image?: string;
  customer?: Customer;
  cast?: Cast;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * 拡張ユーザーインターフェイス
 */
export interface User extends BaseUser {
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_name?: string;
  first_name?: string;
  access_token?: string;
  notes?: string;
  nomination_fee?: number;
  special_nomination_fee?: number;
}

/**
 * オプションのユーザーフィールドを含む拡張インターフェイス
 */
export interface UserWithDetails extends User {
  firstName?: string;
  lastName?: string;
  phone?: string;
  [key: string]: any;
}

/**
 * 顧客プロファイル
 */
export interface CustomerProfile {
  id: number;
  user_id: number;
  nickname: string;
  gender?: string;
  age?: number;
  address?: string;
  preferences?: string;
  notes?: string;
}

/**
 * スタッフプロファイル
 */
export interface StaffProfile {
  id: number;
  user_id: number;
  employee_id?: string;
  department?: string;
  position?: string;
  hire_date?: string;
  salary?: number;
}

/**
 * ドライバープロファイル
 */
export interface DriverProfile {
  id: number;
  user_id: number;
  license_number?: string;
  license_type?: string;
  vehicle_type?: string;
  vehicle_plate?: string;
}

/**
 * キャストプロファイル
 */
export interface CastProfile {
  id: number;
  user_id: number;
  stage_name: string;
  nomination_fee?: number;
  join_date?: string;
  height?: number;
  bust?: number;
  cup?: string;
  waist?: number;
  hip?: number;
  age?: number;
  blood_type?: string;
  hometown?: string;
  hobbies?: string;
  spec?: string;
}

/**
 * ユーザー登録リクエスト
 */
export interface UserRegistrationRequest {
  username: string;
  password: string;
  email: string;
  role: UserRole | string;
  phone_number?: string;
  profile_image?: File;
  [key: string]: any;
}

/**
 * ユーザー一覧レスポンス
 */
export interface UserListResponse {
  data: User[];
  meta: PaginationMeta;
}

/**
 * フィルター用のユーザークエリパラメータ
 */
export interface UserQueryParams {
  search?: string;
  role?: UserRole | string;
  status?: UserStatus | string;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface UserCreateInput {
  username: string;
  email: string;
  password: string;
  role: UserRole;
  first_name: string;
  last_name: string;
  phone?: string;
  is_active?: boolean;
  profile_image?: string;
}

export interface UserUpdateInput extends Partial<Omit<UserCreateInput, 'password'>> {
  password?: string;
}

export interface UserFilters {
  role?: UserRole;
  isActive?: boolean;
  search?: string;
}

export interface UserProfile extends Omit<User, 'password'> {
  notifications_enabled: boolean;
  line_user_id?: string;
  preferred_language: string;
  theme_preference: 'light' | 'dark' | 'system';
}

export interface UserProfileUpdateInput extends Partial<Omit<UserProfile, keyof BaseEntity>> {}

/**
 * ユーザーロールの表示名
 */
export const USER_ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.ADMIN]: '管理者',
  [UserRole.CUSTOMER]: '顧客',
  [UserRole.CAST]: 'キャスト',
  [UserRole.DRIVER]: 'ドライバー',
  [UserRole.DISPATCHER]: '内勤スタッフ',
  [UserRole.MANAGER]: 'マネージャー'
};

/**
 * ユーザーロールの色
 */
export const USER_ROLE_COLORS: Record<UserRole, string> = {
  [UserRole.ADMIN]: 'red',
  [UserRole.CUSTOMER]: 'blue',
  [UserRole.CAST]: 'purple',
  [UserRole.DRIVER]: 'green',
  [UserRole.DISPATCHER]: 'orange', // dispatcherの色をオレンジに変更
  [UserRole.MANAGER]: 'blue'
};

export interface UserFormData {
  id?: number;
  username: string;
  password?: string;
  email: string;
  phone_number: string;
  line_account?: string;
  role: UserRole | string;
  profile_image?: File | null;
  
  // 顧客特有の項目
  customer_type?: 'guest' | 'member';
  nickname?: string;
  gender?: 'male' | 'female' | 'unspecified';
  address?: string;
  
  // キャスト特有の項目
  stage_name?: string;
  options?: string[];
  nomination_fee?: string;
  join_date?: string;
  age?: string;
  height?: string;
  bust?: string;
  cup?: string;
  waist?: string;
  hip?: string;
  spec?: string;
  notes?: string;
}

export type UserEditFormData = Omit<UserFormData, 'username'>;

/**
 * 顧客情報の型定義
 */
export interface Customer {
  id: number;
  customer_type: 'guest' | 'member';
  nickname: string;
  gender: 'male' | 'female' | 'unspecified';
  address: string;
}

/**
 * キャスト情報の型定義
 */
export interface Cast {
  id: number;
  stage_name: string;
  options: string[];
  nomination_fee: string;
  join_date: string;
  age: string;
  height: string;
  bust: string;
  cup: string;
  waist: string;
  hip: string;
  spec: string;
  notes: string;
}

/**
 * ページネーションメタデータ
 */
export interface PaginationMeta {
  current_page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

/**
 * ユーザー詳細レスポンスの型
 */
export interface UserDetailResponse {
  data: User;
}

/**
 * ユーザー作成・更新レスポンスの型
 */
export interface UserMutationResponse {
  data: User;
  message: string;
}
